:root {
  color: #102f49;
  background: #f6e6bd;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  --navy: #0b2f49;
  --navy-deep: #061f31;
  --teal: #087f7c;
  --teal-deep: #04575a;
  --ivory: #fff4d9;
  --paper: #f4dfae;
  --red: #b83a2f;
  --coral: #e7654a;
  --saffron: #e7a91b;
  --leaf: #3f7953;
  --walnut: #6e4029;
  --ink-muted: #6d5a49;
  --display: Georgia, "Times New Roman", serif;
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  min-width: 320px;
  background:
    radial-gradient(circle at 15% 10%, rgba(255, 255, 255, 0.45), transparent 24rem),
    var(--ivory);
  color: var(--navy);
  overflow-x: hidden;
}

body::selection {
  color: var(--ivory);
  background: var(--red);
}

a {
  color: inherit;
  text-decoration: none;
}

button,
a {
  -webkit-tap-highlight-color: transparent;
}

img {
  max-width: 100%;
  display: block;
}

.hero {
  position: relative;
  min-height: 940px;
  background:
    radial-gradient(circle at 76% 34%, rgba(14, 139, 132, 0.38), transparent 25rem),
    linear-gradient(135deg, #07263b 0%, #0a354e 60%, #062a36 100%);
  color: var(--ivory);
  overflow: hidden;
}

.hero::after,
.product-story::before,
.craft::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.18;
  background-image:
    repeating-linear-gradient(0deg, transparent 0 5px, rgba(255,255,255,.035) 5px 6px),
    repeating-linear-gradient(90deg, transparent 0 7px, rgba(255,255,255,.025) 7px 8px);
  mix-blend-mode: soft-light;
}

.site-header {
  position: relative;
  z-index: 20;
  border-bottom: 1px solid rgba(255, 244, 217, 0.18);
}

.header-inner {
  max-width: 1240px;
  height: 92px;
  margin: 0 auto;
  padding: 0 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  transition: opacity 180ms ease;
}

.brand:hover,
.brand:focus-visible {
  opacity: .82;
}

.brand:focus-visible {
  outline: 2px solid var(--saffron);
  outline-offset: 5px;
}

.brand img {
  width: clamp(176px, 17vw, 205px);
  height: auto;
  object-fit: contain;
}

.nav {
  display: flex;
  align-items: center;
  gap: 34px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.055em;
}

.nav a {
  position: relative;
  color: rgba(255, 244, 217, 0.83);
  transition: color 180ms ease;
}

.nav a:not(.nav-status)::after {
  content: "";
  position: absolute;
  left: 0;
  right: 100%;
  bottom: -7px;
  height: 2px;
  background: var(--saffron);
  transition: right 180ms ease;
}

.nav a:hover,
.nav a:focus-visible {
  color: #fff;
}

.nav a:hover::after,
.nav a:focus-visible::after {
  right: 0;
}

.nav-status {
  padding: 12px 16px;
  border: 1px solid rgba(255, 244, 217, 0.28);
  border-radius: 999px;
}

.nav-status span,
.live-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  background: #69bc83;
  box-shadow: 0 0 0 5px rgba(105, 188, 131, 0.14);
}

.menu-toggle {
  display: none;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(255,255,255,.25);
  border-radius: 50%;
  background: transparent;
}

.menu-toggle span {
  display: block;
  width: 18px;
  height: 2px;
  margin: 5px auto;
  background: var(--ivory);
}

.hero-grain {
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background:
    radial-gradient(circle, #fff 0 0.7px, transparent .8px) 0 0 / 8px 8px,
    radial-gradient(circle, #fff 0 0.5px, transparent .6px) 4px 4px / 10px 10px;
}

.hero-atlas {
  position: absolute;
  width: min(760px, 62vw);
  right: -205px;
  top: 120px;
  opacity: 0.27;
  filter: saturate(0.9) contrast(1.05);
  transform: rotate(6deg);
}

.hero-inner {
  position: relative;
  z-index: 3;
  max-width: 1240px;
  min-height: 748px;
  margin: 0 auto;
  padding: 76px 28px 96px;
  display: grid;
  grid-template-columns: minmax(0, 1.07fr) minmax(360px, .93fr);
  align-items: center;
  gap: 32px;
}

.eyebrow,
.kicker {
  margin: 0 0 24px;
  color: var(--coral);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .16em;
  text-transform: uppercase;
}

.eyebrow {
  color: #f2b629;
}

.eyebrow span {
  display: inline-block;
  width: 36px;
  height: 2px;
  margin: 0 13px 3px 0;
  background: #f2b629;
}

.hero h1,
.section h2,
.craft h2,
.final-cta h2 {
  margin: 0;
  font-family: var(--display);
  font-weight: 700;
  letter-spacing: -.035em;
  line-height: .98;
}

.hero h1 {
  max-width: 710px;
  font-size: clamp(60px, 6.4vw, 95px);
  text-wrap: balance;
}

.hero-lede {
  max-width: 660px;
  margin: 30px 0 0;
  color: rgba(255, 244, 217, .76);
  font-size: clamp(18px, 1.6vw, 22px);
  line-height: 1.58;
}

.hero-actions {
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 28px;
}

.button {
  display: inline-flex;
  min-height: 56px;
  padding: 0 24px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: .075em;
  text-transform: uppercase;
  box-shadow: 5px 6px 0 rgba(0,0,0,.17);
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.button:hover,
.button:focus-visible {
  transform: translate(2px, 2px);
  box-shadow: 2px 3px 0 rgba(0,0,0,.22);
}

.button--red {
  color: #fff7e5;
  background: var(--red);
  border: 1px solid #e57c63;
}

.button--navy {
  color: var(--ivory);
  background: var(--navy);
  border: 1px solid #31546b;
}

.text-link {
  color: var(--ivory);
  font-size: 14px;
  font-weight: 750;
  letter-spacing: .02em;
}

.text-link span {
  display: inline-block;
  margin-left: 8px;
  color: var(--saffron);
  transition: transform 180ms ease;
}

.text-link:hover span {
  transform: translate(3px, 3px);
}

.hero-note {
  max-width: 530px;
  margin-top: 39px;
  padding-top: 25px;
  display: flex;
  align-items: flex-start;
  border-top: 1px solid rgba(255,244,217,.18);
}

.hero-note .live-dot {
  flex: 0 0 auto;
  margin-top: 8px;
}

.hero-note p {
  margin: 0;
  color: rgba(255,244,217,.64);
  font-size: 14px;
  line-height: 1.6;
}

.hero-note b {
  color: var(--ivory);
}

.hero-product {
  position: relative;
  min-height: 690px;
  display: grid;
  place-items: center;
}

.phone {
  position: relative;
  padding: 10px;
  border: 1px solid rgba(255,255,255,.16);
  border-radius: 42px;
  background: linear-gradient(145deg, #173a4e, #031620 68%);
  box-shadow: 0 38px 76px rgba(0,0,0,.46), inset 0 0 0 2px #284859;
}

.phone-screen {
  position: relative;
  height: 100%;
  overflow: hidden;
  border-radius: 33px;
  background: #043f42;
}

.phone-screen img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.phone-speaker {
  position: absolute;
  z-index: 4;
  top: 18px;
  left: 50%;
  width: 61px;
  height: 5px;
  border-radius: 999px;
  background: #07151d;
  transform: translateX(-50%);
}

.phone--hero {
  z-index: 3;
  width: 326px;
  height: 686px;
  transform: rotate(2.2deg);
}

.orbit {
  position: absolute;
  border: 1px solid rgba(238,174,29,.32);
  border-radius: 50%;
}

.orbit--one {
  width: 570px;
  height: 570px;
}

.orbit--two {
  width: 455px;
  height: 455px;
  border-style: dashed;
}

.floating-card {
  position: absolute;
  z-index: 4;
  width: 105px;
  filter: drop-shadow(0 22px 19px rgba(0,0,0,.34));
}

.floating-card--one {
  left: 4%;
  top: 16%;
  transform: rotate(-14deg);
}

.floating-card--two {
  right: 0;
  bottom: 17%;
  transform: rotate(12deg);
}

.hero-stamp {
  position: absolute;
  z-index: 5;
  right: 4%;
  top: 5%;
  width: 112px;
  height: 112px;
  display: grid;
  place-items: center;
  border: 2px solid #e56b55;
  border-radius: 50%;
  color: #f39a7c;
  font-family: var(--display);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.05;
  text-align: center;
  text-transform: uppercase;
  transform: rotate(8deg);
}

.hero-stamp::after {
  content: "";
  position: absolute;
  inset: 5px;
  border: 1px dashed #e56b55;
  border-radius: inherit;
}

.trust-strip {
  position: absolute;
  z-index: 6;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 74px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 27px;
  border-top: 1px solid rgba(255,255,255,.16);
  background: rgba(5, 28, 43, .73);
  backdrop-filter: blur(10px);
  color: rgba(255,244,217,.76);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.trust-strip i {
  color: var(--saffron);
  font-style: normal;
}

.section {
  position: relative;
  padding: 132px max(28px, calc((100vw - 1184px) / 2));
}

.section h2,
.craft h2,
.final-cta h2 {
  font-size: clamp(50px, 5.4vw, 78px);
}

.daily {
  display: grid;
  grid-template-columns: .9fr 1.1fr;
  gap: 54px 90px;
  background:
    linear-gradient(90deg, rgba(135,89,48,.035) 1px, transparent 1px) 0 0/28px 28px,
    linear-gradient(rgba(135,89,48,.03) 1px, transparent 1px) 0 0/28px 28px,
    #fff2d4;
}

.daily-intro h2 em {
  color: var(--red);
  font-style: normal;
}

.daily-intro > p:last-child,
.section-heading > p,
.fairness-copy > p,
.craft-copy > p,
.final-cta > p,
.large-copy {
  color: var(--ink-muted);
  font-size: 18px;
  line-height: 1.75;
}

.daily-intro > p:last-child {
  max-width: 530px;
  margin: 31px 0 0;
}

.daily-stage {
  position: relative;
  min-height: 510px;
  display: grid;
  place-items: center;
  isolation: isolate;
}

.daily-stage::before {
  content: "";
  position: absolute;
  inset: 5% -10% -4% 4%;
  z-index: -3;
  border-radius: 47% 53% 53% 47%;
  background: #0d7d77;
  transform: rotate(-3deg);
  box-shadow: 16px 18px 0 #073e55;
}

.daily-paper {
  position: relative;
  width: min(520px, 94%);
  min-height: 366px;
  padding: 32px 36px 30px;
  border: 2px solid rgba(92,59,35,.5);
  border-radius: 10px 24px 12px 21px;
  background:
    radial-gradient(circle at 32% 24%, rgba(255,255,255,.65), transparent 7rem),
    #f8e4b8;
  box-shadow: 10px 14px 0 rgba(7,46,58,.28), 0 25px 60px rgba(15,30,28,.22);
  transform: rotate(1.4deg);
}

.daily-paper::after {
  content: "";
  position: absolute;
  inset: 10px;
  border: 1px dashed rgba(151,74,50,.55);
  border-radius: 5px 16px 6px 13px;
  pointer-events: none;
}

.daily-paper-topline {
  display: flex;
  justify-content: space-between;
  color: var(--red);
  font-size: 10px;
  font-weight: 850;
  letter-spacing: .13em;
  text-transform: uppercase;
}

.daily-paper h3 {
  margin: 47px 0 5px;
  font-family: var(--display);
  font-size: 58px;
  line-height: 1;
}

.daily-paper > p {
  margin: 0;
  color: #754b38;
  font-family: var(--display);
  font-size: 17px;
}

.daily-stats {
  margin: 35px 0 31px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid rgba(82,54,34,.22);
  border-bottom: 1px solid rgba(82,54,34,.22);
}

.daily-stats div {
  padding: 15px 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.daily-stats div + div {
  padding-left: 16px;
  border-left: 1px solid rgba(82,54,34,.22);
}

.daily-stats small {
  color: #896a51;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .1em;
  text-transform: uppercase;
}

.daily-stats strong {
  font-family: var(--display);
  font-size: 21px;
}

.paper-button {
  position: relative;
  z-index: 2;
  min-height: 55px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px;
  background: var(--red);
  color: #fff5dd;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: .09em;
  text-transform: uppercase;
}

.wax-seal {
  position: absolute;
  z-index: 4;
  width: 178px;
  right: -6%;
  bottom: -1%;
  filter: drop-shadow(0 18px 19px rgba(37,28,20,.3));
  transform: rotate(-7deg);
}

.stitched-route {
  position: absolute;
  inset: 0;
}

.stitched-route::before {
  content: "";
  position: absolute;
  width: 86%;
  height: 57%;
  left: 2%;
  top: 21%;
  border: 2px dashed rgba(237,177,27,.8);
  border-radius: 50%;
  transform: rotate(-8deg);
}

.stitched-route span {
  position: absolute;
  z-index: 2;
  width: 10px;
  height: 10px;
  border: 2px solid #fff2d4;
  border-radius: 50%;
  background: var(--saffron);
}

.stitched-route span:nth-child(1) { left: 1%; top: 51%; }
.stitched-route span:nth-child(2) { left: 26%; top: 15%; }
.stitched-route span:nth-child(3) { right: 7%; top: 39%; }
.stitched-route span:nth-child(4) { right: 12%; bottom: 12%; }

.daily-steps {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid rgba(11,47,73,.18);
}

.daily-steps article {
  padding: 32px 32px 0 0;
}

.daily-steps article + article {
  padding-left: 32px;
  border-left: 1px solid rgba(11,47,73,.18);
}

.daily-steps span,
.feature-list b,
.proof-flow article > span {
  color: var(--red);
  font-family: var(--display);
  font-size: 16px;
  font-weight: 800;
}

.daily-steps h3 {
  margin: 18px 0 9px;
  font-family: var(--display);
  font-size: 25px;
}

.daily-steps p {
  max-width: 310px;
  margin: 0;
  color: #75614e;
  line-height: 1.65;
}

.product-story {
  min-height: 950px;
  display: grid;
  grid-template-columns: 1.05fr .95fr;
  align-items: center;
  gap: 90px;
  background:
    radial-gradient(circle at 20% 44%, rgba(13,126,120,.28), transparent 29rem),
    var(--navy-deep);
  color: var(--ivory);
  overflow: hidden;
}

.product-visual {
  position: relative;
  height: 680px;
}

.phone--story {
  position: absolute;
  width: 284px;
  height: 596px;
}

.phone--front {
  z-index: 3;
  left: 24%;
  top: 2%;
  transform: rotate(3deg);
}

.phone--back {
  left: 1%;
  top: 11%;
  opacity: .78;
  transform: rotate(-8deg) scale(.9);
}

.phone--back img {
  object-position: center 16%;
}

.caption-card {
  position: absolute;
  z-index: 5;
  right: 0;
  bottom: 3%;
  width: 285px;
  padding: 25px;
  border: 1px solid rgba(255,244,217,.3);
  background: #f4dfae;
  color: var(--navy);
  box-shadow: 10px 12px 0 rgba(0,0,0,.22);
  transform: rotate(-1deg);
}

.caption-card span {
  color: var(--red);
  font-size: 9px;
  font-weight: 850;
  letter-spacing: .15em;
  text-transform: uppercase;
}

.caption-card strong {
  display: block;
  margin-top: 10px;
  font-family: var(--display);
  font-size: 21px;
  line-height: 1.25;
}

.kicker--light {
  color: var(--saffron);
}

.product-copy h2 {
  max-width: 500px;
}

.large-copy {
  max-width: 565px;
  margin: 30px 0 44px;
  color: rgba(255,244,217,.68);
}

.feature-list {
  max-width: 570px;
  border-top: 1px solid rgba(255,244,217,.15);
}

.feature-list > div {
  padding: 21px 0;
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 16px;
  border-bottom: 1px solid rgba(255,244,217,.15);
}

.feature-list b {
  color: var(--saffron);
}

.feature-list span {
  color: rgba(255,244,217,.6);
  line-height: 1.5;
}

.feature-list strong {
  display: block;
  color: var(--ivory);
  font-size: 14px;
}

.games {
  background: #f4dfae;
}

.section-heading {
  margin-bottom: 66px;
  display: grid;
  grid-template-columns: 1.1fr .7fr;
  align-items: end;
  gap: 80px;
}

.section-heading > p {
  margin: 0 0 3px;
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.game-card {
  position: relative;
  min-height: 285px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid rgba(11,47,73,.32);
  border-top: 5px solid var(--accent, var(--navy));
  background:
    linear-gradient(145deg, rgba(255,255,255,.45), transparent 44%),
    #fff1ce;
  box-shadow: 5px 6px 0 rgba(63,48,36,.09);
  transition: transform 200ms ease, box-shadow 200ms ease;
}

.game-card:hover {
  transform: translateY(-5px) rotate(-.25deg);
  box-shadow: 8px 11px 0 rgba(63,48,36,.13);
}

.game-card--coral { --accent: #df654d; }
.game-card--teal { --accent: #087f7c; }
.game-card--green { --accent: #3f7953; }
.game-card--ink { --accent: #123b59; }
.game-card--red { --accent: #aa3e31; }
.game-card--saffron { --accent: #dea51c; }
.game-card--leaf { --accent: #5d8247; }
.game-card--cobalt { --accent: #17688b; }

.game-topline {
  display: flex;
  justify-content: space-between;
  color: var(--accent);
  font-family: var(--display);
  font-weight: 800;
}

.game-topline i {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 1px dashed var(--accent);
  border-radius: 50%;
  font-style: normal;
}

.game-card h3 {
  margin: 0 0 8px;
  font-family: var(--display);
  font-size: 30px;
  line-height: 1.05;
}

.game-card p {
  margin: 0;
  color: var(--accent);
  font-size: 13px;
  font-weight: 750;
}

.game-card small {
  padding-top: 14px;
  border-top: 1px solid rgba(11,47,73,.16);
  color: #77604a;
  font-size: 9px;
  font-weight: 850;
  letter-spacing: .13em;
  text-transform: uppercase;
}

.catalog-note {
  margin: 30px 0 0;
  color: #795f45;
  font-family: var(--display);
  font-size: 16px;
  font-style: italic;
  text-align: right;
}

.fairness {
  display: grid;
  grid-template-columns: .82fr 1.18fr;
  gap: 90px;
  background: #fff5dc;
}

.kicker--red {
  color: var(--red);
}

.fairness-copy > p {
  margin: 30px 0;
}

.proof-footnote {
  padding: 17px 0;
  display: flex;
  align-items: center;
  gap: 15px;
  border-top: 1px solid rgba(11,47,73,.18);
  border-bottom: 1px solid rgba(11,47,73,.18);
}

.proof-footnote span {
  padding: 7px 9px;
  border-radius: 3px;
  background: var(--navy);
  color: var(--ivory);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
}

.proof-footnote p {
  margin: 0;
  color: #725f4e;
  font-size: 13px;
}

.proof-flow {
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.proof-flow article {
  min-height: 203px;
  padding: 24px;
  display: grid;
  grid-template-columns: 40px 1fr;
  align-content: start;
  gap: 13px;
  border: 1px solid rgba(11,47,73,.27);
  background: #f4dfae;
  box-shadow: 5px 6px 0 rgba(91,59,37,.08);
}

.proof-flow article:nth-child(4) {
  color: var(--ivory);
  background: var(--red);
}

.proof-flow article:nth-child(4) > span,
.proof-flow article:nth-child(4) p {
  color: rgba(255,244,217,.78);
}

.proof-flow h3 {
  margin: 0;
  font-family: var(--display);
  font-size: 25px;
}

.proof-flow p {
  margin: 10px 0 0;
  color: #75614f;
  font-size: 13px;
  line-height: 1.6;
}

.proof-flow img {
  position: absolute;
  z-index: 3;
  width: 145px;
  left: 50%;
  top: 50%;
  filter: drop-shadow(0 14px 18px rgba(70,34,26,.24));
  transform: translate(-50%, -50%) rotate(-8deg);
  pointer-events: none;
}

.craft {
  position: relative;
  min-height: 730px;
  padding: 112px max(28px, calc((100vw - 1184px) / 2));
  display: grid;
  grid-template-columns: 1.06fr .94fr;
  align-items: center;
  gap: 90px;
  background:
    radial-gradient(circle at 24% 45%, rgba(18,131,119,.38), transparent 30rem),
    #073949;
  color: var(--ivory);
  overflow: hidden;
}

.craft-art {
  position: relative;
  min-height: 485px;
  display: grid;
  place-items: center;
}

.craft-art::before,
.craft-art::after {
  content: "";
  position: absolute;
  border: 1px dashed rgba(231,169,27,.45);
  border-radius: 50%;
}

.craft-art::before { width: 470px; height: 470px; }
.craft-art::after { width: 370px; height: 370px; }

.craft-art img {
  position: relative;
  z-index: 2;
  width: min(610px, 100%);
  filter: drop-shadow(0 30px 26px rgba(0,0,0,.3));
}

.craft-copy h2 em {
  color: #f07759;
  font-style: normal;
}

.craft-copy > p {
  max-width: 550px;
  margin: 29px 0 37px;
  color: rgba(255,244,217,.7);
}

.material-line {
  display: flex;
  align-items: center;
  gap: 14px;
  color: #efbf4e;
  font-family: var(--display);
  font-size: 16px;
  font-weight: 700;
}

.material-line i {
  width: 20px;
  height: 1px;
  background: rgba(255,244,217,.28);
}

.final-cta {
  position: relative;
  padding: 130px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background:
    radial-gradient(circle at 50% 40%, rgba(8, 127, 124, .34), transparent 25rem),
    linear-gradient(145deg, #082b42, #061f31 68%);
  color: var(--ivory);
  text-align: center;
  overflow: hidden;
}

.final-cta::before,
.final-cta::after {
  content: "";
  position: absolute;
  width: 220px;
  height: 220px;
  border: 1px dashed rgba(231,169,27,.34);
  border-radius: 50%;
}

.final-cta::before { left: -75px; top: 70px; }
.final-cta::after { right: -60px; bottom: 20px; }

.final-cta > .final-lockup {
  width: min(430px, 82vw);
  margin-bottom: 34px;
}

.final-cta .kicker {
  margin-bottom: 18px;
  color: var(--saffron);
}

.final-cta > p {
  max-width: 660px;
  margin: 27px auto 36px;
  color: rgba(255, 244, 217, .7);
}

footer {
  min-height: 142px;
  padding: 35px max(28px, calc((100vw - 1184px) / 2));
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 36px;
  border-top: 1px solid rgba(255,244,217,.16);
  background: #061f31;
  color: var(--ivory);
}

footer > p,
footer > small {
  color: rgba(255,244,217,.54);
  font-size: 11px;
}

.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 650ms ease, transform 650ms cubic-bezier(.2,.75,.25,1);
  transition-delay: var(--delay, 0ms);
}

.reveal--late {
  transition-delay: 120ms;
}

.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 1040px) {
  .hero-inner {
    grid-template-columns: 1fr .75fr;
  }

  .hero-product {
    transform: translateX(35px) scale(.9);
  }

  .games-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .product-story,
  .craft,
  .fairness {
    gap: 48px;
  }

  .caption-card {
    right: -5%;
  }
}

@media (max-width: 820px) {
  .hero {
    min-height: auto;
  }

  .header-inner {
    height: 78px;
  }

  .menu-toggle {
    display: block;
    position: relative;
    z-index: 2;
  }

  .nav {
    position: absolute;
    left: 16px;
    right: 16px;
    top: 68px;
    padding: 24px;
    display: none;
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    border: 1px solid rgba(255,255,255,.18);
    background: rgba(5,29,43,.96);
    box-shadow: 0 18px 45px rgba(0,0,0,.28);
  }

  .nav--open {
    display: flex;
  }

  .hero-inner {
    padding-top: 70px;
    grid-template-columns: 1fr;
    text-align: center;
  }

  .hero-copy {
    position: relative;
    z-index: 5;
  }

  .hero h1,
  .hero-lede,
  .hero-note {
    margin-left: auto;
    margin-right: auto;
  }

  .hero-actions,
  .hero-note {
    justify-content: center;
  }

  .hero-product {
    min-height: 650px;
    transform: none;
  }

  .hero-atlas {
    width: 780px;
    right: -380px;
    top: 290px;
  }

  .trust-strip {
    position: relative;
    flex-wrap: wrap;
    gap: 13px 18px;
  }

  .section,
  .craft {
    padding-top: 96px;
    padding-bottom: 96px;
  }

  .daily,
  .product-story,
  .fairness,
  .craft,
  .section-heading {
    grid-template-columns: 1fr;
  }

  .daily {
    gap: 55px;
  }

  .daily-intro {
    text-align: center;
  }

  .daily-intro > p:last-child {
    margin-left: auto;
    margin-right: auto;
  }

  .daily-steps {
    grid-template-columns: 1fr;
  }

  .daily-steps article,
  .daily-steps article + article {
    padding: 25px 0;
    border-left: 0;
    border-bottom: 1px solid rgba(11,47,73,.18);
  }

  .product-story {
    gap: 25px;
  }

  .product-visual {
    max-width: 620px;
    width: 100%;
    margin: 0 auto;
  }

  .product-copy {
    max-width: 650px;
    margin: 0 auto;
    text-align: center;
  }

  .feature-list {
    text-align: left;
  }

  .section-heading {
    gap: 26px;
  }

  .fairness-copy,
  .craft-copy {
    max-width: 650px;
  }

  .craft-art {
    order: 2;
  }

  footer {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
}

@media (max-width: 560px) {
  .header-inner {
    padding: 0 18px;
  }

  .brand img {
    width: 166px;
  }

  .hero-inner {
    padding: 54px 18px 62px;
  }

  .hero h1 {
    font-size: clamp(48px, 14vw, 66px);
  }

  .eyebrow span {
    display: none;
  }

  .hero-actions {
    flex-direction: column;
    gap: 22px;
  }

  .hero-product {
    min-height: 565px;
  }

  .phone--hero {
    width: 264px;
    height: 556px;
  }

  .orbit--one { width: 440px; height: 440px; }
  .orbit--two { width: 350px; height: 350px; }

  .floating-card {
    width: 80px;
  }

  .floating-card--one { left: -3%; top: 11%; }
  .floating-card--two { right: -5%; bottom: 12%; }

  .hero-stamp {
    width: 87px;
    height: 87px;
    right: 0;
    top: 3%;
    font-size: 12px;
  }

  .trust-strip i:nth-of-type(2),
  .trust-strip i:nth-of-type(3),
  .trust-strip span:nth-of-type(3),
  .trust-strip span:nth-of-type(4) {
    display: none;
  }

  .section,
  .craft {
    padding-left: 20px;
    padding-right: 20px;
  }

  .section h2,
  .craft h2,
  .final-cta h2 {
    font-size: clamp(44px, 12vw, 60px);
  }

  .daily-stage {
    min-height: 450px;
  }

  .daily-stage::before {
    inset: 5% -9% 1% -2%;
  }

  .daily-paper {
    padding: 25px 24px 24px;
  }

  .daily-paper h3 {
    font-size: 45px;
  }

  .daily-paper-topline span:last-child {
    display: none;
  }

  .daily-stats strong {
    font-size: 17px;
  }

  .wax-seal {
    width: 126px;
    right: -8%;
  }

  .product-visual {
    height: 540px;
  }

  .phone--story {
    width: 224px;
    height: 470px;
  }

  .phone--front { left: 28%; }
  .phone--back { left: -5%; }

  .caption-card {
    width: 225px;
    padding: 19px;
    bottom: 1%;
  }

  .caption-card strong {
    font-size: 17px;
  }

  .games-grid,
  .proof-flow {
    grid-template-columns: 1fr;
  }

  .game-card {
    min-height: 245px;
  }

  .proof-flow article {
    min-height: auto;
  }

  .proof-flow img {
    display: none;
  }

  .craft-art {
    min-height: 390px;
  }

  .craft-art::before { width: 360px; height: 360px; }
  .craft-art::after { width: 285px; height: 285px; }

  .material-line {
    flex-wrap: wrap;
  }

  .final-cta {
    padding: 95px 20px;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    transition-duration: .01ms !important;
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
  }
}
